import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from "recharts";
import API_BASE_URL from "../apiBaseUrl";

const COLORS = ["#28a745", "#dc3545"];

const C = {
  bg: "#fff",
  panel: "#fafafa",
  line: "#e0e0e0",
  ink: "#000",
  muted: "#555",
  yellow: "#FFC107",
};

function driverDisplayName(d) {
  const parts = [
    d.firstName,
    d.secondName,
    d.surname && String(d.surname).toLowerCase() !== "null" ? d.surname : "",
  ].filter(Boolean);
  const joined = parts.join(" ").trim();
  return joined || "—";
}

/**
 * Home: fleet & driver snapshot from Vehicle / Driver Management (onboarding APIs),
 * plus live GPS counts when available. Monthly spreadsheet lives at /monthly-data-entry only.
 */
const Home = () => {
  const [fleetRecords, setFleetRecords] = useState([]);
  const [driversList, setDriversList] = useState([]);
  const [fleetLoading, setFleetLoading] = useState(true);
  const [query, setQuery] = useState("");

  const [liveVehicles, setLiveVehicles] = useState([]);
  const [stoppedCount, setStoppedCount] = useState(0);
  const [movingCount, setMovingCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setFleetLoading(true);
      let vehicles = [];
      let drivers = [];
      if (!API_BASE_URL) {
        if (!cancelled) {
          setFleetRecords([]);
          setDriversList([]);
          setFleetLoading(false);
        }
        return;
      }
      try {
        const vRes = await axios.get(`${API_BASE_URL}/onboarding/all-vehicles`);
        vehicles = Array.isArray(vRes.data) ? vRes.data : [];
      } catch (e) {
        if (e.response?.status !== 404) console.warn("Home: vehicles", e.message);
      }
      try {
        const dRes = await axios.get(`${API_BASE_URL}/onboarding/drivers`);
        drivers = Array.isArray(dRes.data) ? dRes.data : [];
      } catch (e) {
        try {
          const d2 = await axios.get(`${API_BASE_URL}/onboarding/driver`);
          drivers = Array.isArray(d2.data) ? d2.data : [];
        } catch (e2) {
          if (e.response?.status !== 404 && e2.response?.status !== 404) {
            console.warn("Home: drivers", e2.message);
          }
        }
      }
      if (!cancelled) {
        setFleetRecords(vehicles);
        setDriversList(drivers);
        setFleetLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const url = API_BASE_URL ? `${API_BASE_URL}/vehicle/fetch-locations` : null;
    if (!url) return;
    fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error(String(r.status));
        return r.json();
      })
      .then((data) => {
        if (!Array.isArray(data) || data.length === 0) return;
        setLiveVehicles(data);
        const stopped = data.filter((v) => Number(v.speed) === 0).length;
        const moving = data.filter((v) => Number(v.speed) > 0).length;
        setStoppedCount(stopped);
        setMovingCount(moving);
      })
      .catch(() => {});
  }, []);

  const q = query.trim().toLowerCase();
  const filteredVehicles = useMemo(() => {
    const list = [...fleetRecords].sort((a, b) =>
      String(a.vehicleNumber || "").localeCompare(String(b.vehicleNumber || ""), undefined, {
        numeric: true,
      })
    );
    if (!q) return list;
    return list.filter((v) => {
      const hay = [v.vehicleNumber, v.registerName, v.vehicleType]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [fleetRecords, q]);

  const filteredDrivers = useMemo(() => {
    const list = [...driversList].sort((a, b) =>
      String(a.driverId || "").localeCompare(String(b.driverId || ""), undefined, { numeric: true })
    );
    if (!q) return list;
    return list.filter((d) => {
      const hay = [d.driverId, driverDisplayName(d), d.contactNumber]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [driversList, q]);

  const pieData = [
    { name: "Moving", value: movingCount },
    { name: "Stopped", value: stoppedCount },
  ].filter((d) => d.value > 0);

  const wrap = {
    marginLeft: typeof window !== "undefined" && window.innerWidth <= 768 ? 0 : 260,
    marginRight: 12,
    paddingBottom: 32,
    fontFamily: "Arial, sans-serif",
    color: C.ink,
    background: C.bg,
    minHeight: "calc(100vh - 70px)",
    boxSizing: "border-box",
  };

  const th = {
    background: C.ink,
    color: "#fff",
    padding: "10px 8px",
    textAlign: "left",
    fontSize: 11,
    fontWeight: "bold",
    whiteSpace: "nowrap",
  };
  const td = {
    border: `1px solid ${C.line}`,
    padding: "8px",
    fontSize: 12,
    verticalAlign: "top",
  };

  return (
    <div style={wrap}>
      <h2
        style={{
          background: C.yellow,
          color: C.ink,
          padding: "14px 24px",
          fontSize: "1.05rem",
          fontWeight: "bold",
          textTransform: "uppercase",
          letterSpacing: 1,
          margin: 0,
        }}
      >
        Home — Fleet & drivers
      </h2>

      <div style={{ padding: "16px 20px" }}>
        {!API_BASE_URL && (
          <p style={{ color: C.muted, fontSize: 14 }}>
            Set <code>REACT_APP_API_URL</code> to load Vehicle Management and Driver Management data.
          </p>
        )}

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 12,
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <input
            type="search"
            placeholder="Search vehicles or drivers…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{
              flex: "1 1 220px",
              maxWidth: 360,
              padding: "8px 10px",
              border: `1.5px solid ${C.ink}`,
              borderRadius: 3,
              fontSize: 13,
              fontFamily: "Arial, sans-serif",
            }}
          />
          <Link
            to="/monthly-data-entry"
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "#0066cc",
            }}
          >
            Monthly diesel spreadsheet →
          </Link>
          <Link to="/vehicle-onboarding" style={{ fontSize: 13, fontWeight: 600, color: "#0066cc" }}>
            Vehicle Management →
          </Link>
          <Link to="/driver-onboarding" style={{ fontSize: 13, fontWeight: 600, color: "#0066cc" }}>
            Driver Management →
          </Link>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
            gap: 12,
            marginBottom: 20,
          }}
        >
          {[
            { lbl: "Vehicles (onboarded)", val: fleetRecords.length },
            { lbl: "Drivers (onboarded)", val: driversList.length },
          ].map((s) => (
            <div
              key={s.lbl}
              style={{
                background: C.bg,
                border: `1.5px solid ${C.ink}`,
                borderRadius: 4,
                padding: "14px 16px",
                borderTop: `4px solid ${C.yellow}`,
              }}
            >
              <div style={{ fontFamily: "monospace", fontSize: "1.35rem", fontWeight: 700 }}>{s.val}</div>
              <div
                style={{
                  fontSize: 11,
                  color: C.muted,
                  marginTop: 6,
                  textTransform: "uppercase",
                  letterSpacing: ".04em",
                }}
              >
                {s.lbl}
              </div>
            </div>
          ))}
        </div>

        {fleetLoading ? (
          <p style={{ color: C.muted }}>Loading vehicles and drivers…</p>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 420px), 1fr))",
              gap: 16,
              alignItems: "start",
            }}
          >
            <section
              style={{
                background: C.bg,
                border: `1.5px solid ${C.ink}`,
                borderRadius: 4,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  padding: "10px 14px",
                  background: C.panel,
                  borderBottom: `1px solid ${C.line}`,
                  fontWeight: "bold",
                  fontSize: 13,
                }}
              >
                Vehicles
              </div>
              <div style={{ maxHeight: 360, overflow: "auto" }}>
                {filteredVehicles.length === 0 ? (
                  <div style={{ padding: 16, color: C.muted, fontSize: 13 }}>
                    No vehicles match.{" "}
                    <Link to="/vehicle-onboarding" style={{ color: "#0066cc" }}>
                      Add in Vehicle Management
                    </Link>
                  </div>
                ) : (
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th style={th}>Vehicle no.</th>
                        <th style={th}>Owner / register</th>
                        <th style={th}>Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredVehicles.map((v, i) => (
                        <tr key={v._id || v.vehicleNumber || i} style={{ background: i % 2 ? C.panel : C.bg }}>
                          <td style={{ ...td, fontFamily: "ui-monospace, monospace", fontWeight: 700 }}>
                            {v.vehicleNumber || "—"}
                          </td>
                          <td style={td}>{v.registerName || "—"}</td>
                          <td style={td}>{v.vehicleType || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </section>

            <section
              style={{
                background: C.bg,
                border: `1.5px solid ${C.ink}`,
                borderRadius: 4,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  padding: "10px 14px",
                  background: C.panel,
                  borderBottom: `1px solid ${C.line}`,
                  fontWeight: "bold",
                  fontSize: 13,
                }}
              >
                Drivers
              </div>
              <div style={{ maxHeight: 360, overflow: "auto" }}>
                {filteredDrivers.length === 0 ? (
                  <div style={{ padding: 16, color: C.muted, fontSize: 13 }}>
                    No drivers match.{" "}
                    <Link to="/driver-onboarding" style={{ color: "#0066cc" }}>
                      Add in Driver Management
                    </Link>
                  </div>
                ) : (
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th style={th}>Driver ID</th>
                        <th style={th}>Name</th>
                        <th style={th}>Contact</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDrivers.map((d, i) => (
                        <tr key={d._id || d.driverId || i} style={{ background: i % 2 ? C.panel : C.bg }}>
                          <td style={{ ...td, fontFamily: "ui-monospace, monospace", fontWeight: 700 }}>
                            {d.driverId || "—"}
                          </td>
                          <td style={td}>{driverDisplayName(d)}</td>
                          <td style={td}>{d.contactNumber || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </section>
          </div>
        )}

        <div
          style={{
            marginTop: 28,
            borderTop: `2px solid ${C.line}`,
            paddingTop: 20,
          }}
        >
          <h3 style={{ fontSize: 17, marginBottom: 12 }}>Live fleet overview</h3>
          <p style={{ fontSize: 12, color: C.muted, marginTop: -6, marginBottom: 12 }}>
            From GPS / tracking (<code>/vehicle/fetch-locations</code>), not onboarding.
          </p>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 24,
              alignItems: "center",
            }}
          >
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              <div
                style={{
                  padding: "10px 16px",
                  border: `1px solid ${C.ink}`,
                  borderRadius: 6,
                }}
              >
                Moving: <strong>{movingCount}</strong>
              </div>
              <div
                style={{
                  padding: "10px 16px",
                  border: `1px solid ${C.ink}`,
                  borderRadius: 6,
                }}
              >
                Stopped: <strong>{stoppedCount}</strong>
              </div>
              <div
                style={{
                  padding: "10px 16px",
                  border: `1px solid ${C.ink}`,
                  borderRadius: 6,
                }}
              >
                Tracked units: <strong>{liveVehicles.length}</strong>
              </div>
            </div>
            {pieData.length > 0 ? (
              <div style={{ width: 280, height: 220 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={70}
                      label
                    >
                      {pieData.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <span style={{ color: "#888" }}>No moving/stopped breakdown loaded yet.</span>
            )}
            <Link to="/live-fleet-tracking" style={{ color: "#0066cc", fontWeight: 600 }}>
              Open full live fleet map →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
